import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IncidentSeverityImpact, IncidentSeverityImpactFormData, IncidentSeverityImpactService, PERSONAL_INJURY_OPTIONS, FINANCIAL_IMPACT_OPTIONS } from 'src/app/api/incident-severity-impact.service';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

@Component({
    selector: 'app-severity-impact-form',
    templateUrl: './severity-impact-form.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SeverityImpactFormComponent implements OnInit {

  @Input()
  workItemId: number = 0;

  @Input()
  severityImpact: IncidentSeverityImpact | null = null;

  @Output()
  submit = new EventEmitter<IncidentSeverityImpactFormData>();

  @Output()
  cancel = new EventEmitter<void>();

  editMode = signal(false);
  existingData: WritableSignal<IncidentSeverityImpact | null> = signal(null);

  personalInjuryOptions = PERSONAL_INJURY_OPTIONS;
  financialImpactOptions = FINANCIAL_IMPACT_OPTIONS;

  severityImpactForm: UntypedFormGroup = this.fb.group({
    personalInjuryLevel: [null, Validators.required],
    financialImpactLevel: [null, Validators.required],
    financialAmount: [null],
    additionalNotes: ['']
  });

  constructor(
    private fb: UntypedFormBuilder,
    private severityImpactService: IncidentSeverityImpactService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) { }

  ngOnInit(): void {
    // Get workItemId and severityImpact from modal config if not provided as input
    if (this.config.data?.workItemId) {
      this.workItemId = this.config.data.workItemId;
    }
    if (this.config.data?.severityImpact !== undefined) {
      this.severityImpact = this.config.data.severityImpact;
    }
    
    this.initializeForm();
  }

  private initializeForm() {
    if (this.severityImpact) {
      // We have existing data, populate the form
      this.existingData.set(this.severityImpact);
      this.editMode.set(true);
      this.severityImpactForm.patchValue({
        personalInjuryLevel: this.severityImpact.personalInjuryLevel,
        financialImpactLevel: this.severityImpact.financialImpactLevel,
        financialAmount: this.severityImpact.financialAmount,
        additionalNotes: this.severityImpact.additionalNotes || ''
      });
    } else {
      // No existing data, stay in create mode
      this.existingData.set(null);
      this.editMode.set(false);
    }
  }

  onSubmit() {
    if (this.severityImpactForm.valid) {
      const formData = this.severityImpactForm.value;
      
      // Clear financial amount if financial impact level doesn't require it
      if (formData.financialImpactLevel !== 3) {
        formData.financialAmount = null;
      }
      
      // Convert financial amount to number if provided
      if (formData.financialAmount) {
        formData.financialAmount = Number(formData.financialAmount);
      }

      this.ref.close(formData);
    }
  }

  onCancel() {
    this.ref.close(null);
  }

  getFormControl(name: string) {
    return this.severityImpactForm.get(name);
  }

  shouldShowFinancialAmount(): boolean {
    const financialLevel = this.severityImpactForm.get('financialImpactLevel')?.value;
    return financialLevel === 3; // Significant impact
  }
} 